import AccountCombobox from "@app/components/account-combobox";
import type { AccountOption } from "@app/components/account-combobox";
import StakeholderCombobox from "@app/components/stakeholder-combobox";
import type { StakeholderOption } from "@app/components/stakeholder-combobox";
import { useToast } from "@app/components/toast";
import TxnTablePanel, {
  BulkAction,
  TXN_PANEL_DIALOG_CLASS,
  useTxnSort,
} from "@app/components/txn-table-panel";
import { RECLASSIFY_OPTIONS } from "@app/components/txn-table-panel/use-txn-bulk-actions";
import { useTxnPanelRefresh } from "@app/components/txn-table-panel/use-txn-panel-refresh";
import { getPerspectiveId } from "@app/lib/perspective";
import { environment } from "@app/relay/relay-environment";
import * as Dialog from "@radix-ui/react-dialog";
import { Suspense, useState } from "react";
import { commitMutation, graphql, useLazyLoadQuery } from "react-relay";

import type {
  cashPositionTxnsPanel_Query,
  TxnFlowEnum,
  TxnClassificationEnum,
} from "@app/queries/cashPositionTxnsPanel_Query.graphql";
import type { cashPositionTxnsPanel_MarkTxnsInternalMutation } from "@app/queries/cashPositionTxnsPanel_MarkTxnsInternalMutation.graphql";
import type { cashPositionTxnsPanel_AttributeTxnsMutation } from "@app/queries/cashPositionTxnsPanel_AttributeTxnsMutation.graphql";
import type { cashPositionTxnsPanel_ClassifyTxnsMutation } from "@app/queries/cashPositionTxnsPanel_ClassifyTxnsMutation.graphql";

// The cash-position bar categories that drill down. Each maps to the exact
// `transactions` filter that reproduces the clicked bar: revenue/cost are the
// operating corpus (flow + exclude_distributions); loan proceeds, loan repayments,
// dividends, and tax payments are classification-defined (they sit outside the
// operating gate).
export type CashFlowCategory = "revenue" | "cost" | "loanProceeds" | "loanRepayments" | "dividends" | "tax";

export type CashFlowDrill = {
  category: CashFlowCategory;
  month: string; // YYYY-MM
  label: string; // e.g. "Costs · 2025-04"
};

type PanelVariables = {
  month: string;
  flow: TxnFlowEnum | null;
  classification: TxnClassificationEnum | null;
  excludeDistributions: boolean | null;
};

// The filter that makes the listed txns sum to exactly the clicked bar.
function variablesFor({ category, month }: CashFlowDrill): PanelVariables {
  switch (category) {
    case "revenue":
      return { month, flow: "REVENUE", classification: null, excludeDistributions: true };
    case "cost":
      return { month, flow: "COST", classification: null, excludeDistributions: true };
    case "loanProceeds":
      return { month, flow: null, classification: "LOAN_PROCEEDS", excludeDistributions: null };
    case "loanRepayments":
      return { month, flow: null, classification: "LOAN_REPAYMENT", excludeDistributions: null };
    case "dividends":
      return { month, flow: null, classification: "DIVIDEND", excludeDistributions: null };
    case "tax":
      return { month, flow: null, classification: "TAX_PAYMENT", excludeDistributions: null };
  }
}

// Reclassify targets come from the shared registry (R7 — one canonical set across
// every panel, replacing this panel's former TAX_PAYMENT-only list).

// The bar drill-down: the transactions that sum to the clicked bar, reviewable
// and fixable in place. "Mark internal" removes a txn from the flow; "Set
// counterparty" attributes it; "Reclassify" overrides its metric treatment.
// All three re-derive server-side (Pipeline::Delta), so onChanged() refreshes
// the chart afterwards.
export default function CashPositionTxnsPanel({
  drill,
  accountOptions,
  stakeholderOptions,
  onClose,
  onChanged,
}: {
  drill: CashFlowDrill;
  accountOptions: readonly AccountOption[];
  stakeholderOptions: readonly StakeholderOption[];
  onClose: () => void;
  onChanged: () => void;
}) {
  return (
    <Dialog.Root open onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/20 z-40" />
        <Dialog.Content className={TXN_PANEL_DIALOG_CLASS}>
          <Suspense fallback={<div className="p-6 text-sm text-gray-400">Loading transactions…</div>}>
            <PanelContent
              drill={drill}
              accountOptions={accountOptions}
              stakeholderOptions={stakeholderOptions}
              onClose={onClose}
              onChanged={onChanged}
            />
          </Suspense>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

const panelQuery = graphql`
  query cashPositionTxnsPanel_Query(
    $month: String
    $flow: TxnFlowEnum
    $classification: TxnClassificationEnum
    $excludeDistributions: Boolean
    $perspectiveId: ID
    $sort: TxnSortEnum
    $sortDirection: String
  ) {
    transactions(
      month: $month
      flow: $flow
      classification: $classification
      excludeDistributions: $excludeDistributions
      perspectiveId: $perspectiveId
      sort: $sort
      sortDirection: $sortDirection
    ) {
      totalCount
      totalAmount
      limit
      txns {
        txn {
          id
        }
        md5
        postingDate
        description
        amount
        kind
        accountNumber
        internal
        qboWitnessed
        classification
        expenseNature {
          id
          name
        }
        counterparty {
          name
        }
        category
      }
    }
  }
`;

const markTxnsInternalMutation = graphql`
  mutation cashPositionTxnsPanel_MarkTxnsInternalMutation($input: MarkTxnsInternalInputObject!) {
    markTxnsInternal(input: $input) {
      updatedCount
      errors {
        message
      }
    }
  }
`;

const attributeTxnsMutation = graphql`
  mutation cashPositionTxnsPanel_AttributeTxnsMutation($input: AttributeTxnsInputObject!) {
    attributeTxns(input: $input) {
      stakeholder {
        id
        name
      }
      errors {
        message
      }
    }
  }
`;

const classifyTxnsMutation = graphql`
  mutation cashPositionTxnsPanel_ClassifyTxnsMutation($input: ClassifyTxnsInputObject!) {
    classifyTxns(input: $input) {
      updatedCount
      errors {
        message
      }
    }
  }
`;

function PanelContent({
  drill,
  accountOptions,
  stakeholderOptions,
  onClose,
  onChanged,
}: {
  drill: CashFlowDrill;
  accountOptions: readonly AccountOption[];
  stakeholderOptions: readonly StakeholderOption[];
  onClose: () => void;
  onChanged: () => void;
}) {
  const notify = useToast();
  const { refreshing, refresh, queryOptions } = useTxnPanelRefresh();
  const [busy, setBusy] = useState(false);
  const { sort, direction, onSort, sorting, sortVars } = useTxnSort();

  const vars = variablesFor(drill);
  const data = useLazyLoadQuery<cashPositionTxnsPanel_Query>(
    panelQuery,
    { ...vars, perspectiveId: getPerspectiveId() ?? undefined, ...sortVars },
    queryOptions
  );
  const rows = data.transactions.txns;

  // The mutations re-derive downstream (Pipeline::Delta) server-side, so once they
  // complete both this list and the chart need fresh data.
  const afterMutation = (clearSelection: () => void) => {
    clearSelection();
    setBusy(false);
    refresh();
    onChanged();
  };

  const fail = (message: string) => {
    setBusy(false);
    notify({ message, kind: "error" });
  };

  const markInternal = (txnIds: string[], clearSelection: () => void, choice: { bankAccountId?: string; name?: string }) => {
    setBusy(true);
    commitMutation<cashPositionTxnsPanel_MarkTxnsInternalMutation>(environment, {
      mutation: markTxnsInternalMutation,
      variables: {
        input: { txnIds, destinationBankAccountId: choice.bankAccountId, destinationName: choice.name },
      },
      onCompleted: (res) => {
        const payload = res.markTxnsInternal;
        if (payload?.errors?.length) return fail(payload.errors[0].message);
        notify({ message: `Marked ${txnIds.length} as internal (removed from this flow).`, kind: "success" });
        afterMutation(clearSelection);
      },
      onError: () => fail("Could not mark internal — please try again."),
    });
  };

  const attribute = (txnIds: string[], clearSelection: () => void, choice: { stakeholderId?: string; name?: string }) => {
    setBusy(true);
    commitMutation<cashPositionTxnsPanel_AttributeTxnsMutation>(environment, {
      mutation: attributeTxnsMutation,
      variables: {
        input: {
          txnIds,
          stakeholderId: choice.stakeholderId,
          name: choice.name,
          perspectiveId: getPerspectiveId() ?? undefined,
        },
      },
      onCompleted: (res) => {
        const payload = res.attributeTxns;
        if (payload?.errors?.length) return fail(payload.errors[0].message);
        notify({ message: `Set the counterparty on ${txnIds.length} transaction(s).`, kind: "success" });
        afterMutation(clearSelection);
      },
      onError: () => fail("Could not attribute — please try again."),
    });
  };

  // Override the metric treatment (loan / dividend / tax / fraud), or clear it
  // back to a normal operating txn. Re-derives server-side.
  const reclassify = (txnIds: string[], clearSelection: () => void, classification: TxnClassificationEnum | null) => {
    setBusy(true);
    commitMutation<cashPositionTxnsPanel_ClassifyTxnsMutation>(environment, {
      mutation: classifyTxnsMutation,
      variables: { input: { txnIds, classification } },
      onCompleted: (res) => {
        const payload = res.classifyTxns;
        if (payload?.errors?.length) return fail(payload.errors[0].message);
        notify({ message: `Reclassified ${txnIds.length} transaction(s).`, kind: "success" });
        afterMutation(clearSelection);
      },
      onError: () => fail("Could not reclassify — please try again."),
    });
  };

  const exportParams: Record<string, string> = {
    month: vars.month,
    sort: sortVars.sort,
    sort_direction: sortVars.sortDirection,
  };
  if (vars.flow) exportParams.flow = vars.flow;
  if (vars.classification) exportParams.classification = vars.classification;
  if (vars.excludeDistributions) exportParams.exclude_distributions = "true";
  const perspectiveId = getPerspectiveId();
  if (perspectiveId) exportParams.perspective_id = perspectiveId;

  return (
    <TxnTablePanel
      title={drill.label}
      totalCount={data.transactions.totalCount}
      headerTotalDollars={data.transactions.totalAmount}
      rows={rows}
      emptyLabel="No transactions in this bar."
      sort={sort}
      direction={direction}
      onSort={onSort} sorting={sorting}
      exportParams={exportParams}
      busy={busy || refreshing}
      onClose={onClose}
      renderBulkActions={(selectedIds, clearSelection) => (
        <>
          <BulkAction label="Mark internal" disabled={busy}>
            {(close) => (
              <AccountCombobox
                options={accountOptions}
                placeholder="The owned account on the other end…"
                onPick={(choice) => {
                  close();
                  markInternal(selectedIds, clearSelection, choice);
                }}
              />
            )}
          </BulkAction>
          <BulkAction label="Set counterparty" disabled={busy}>
            {(close) => (
              <StakeholderCombobox
                options={stakeholderOptions}
                onPick={(choice) => {
                  close();
                  attribute(selectedIds, clearSelection, choice);
                }}
              />
            )}
          </BulkAction>
          <BulkAction label="Reclassify" disabled={busy}>
            {(close) => (
              <ul className="min-w-[200px] text-sm">
                {RECLASSIFY_OPTIONS.map((option) => (
                  <li key={option.label}>
                    <button
                      type="button"
                      className="w-full text-left px-2 py-1.5 rounded hover:bg-gray-100"
                      onClick={() => {
                        close();
                        reclassify(selectedIds, clearSelection, option.value);
                      }}
                    >
                      {option.label}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </BulkAction>
        </>
      )}
    />
  );
}
